/**
 * Optional staff step — invite the first employees (#1853, #1498).
 *
 * Skippable on purpose: a one-person workshop is a real case, and a missing
 * colleague blocks nothing. A wizard that will not let you past a step you do
 * not need is worse than one left unfinished.
 *
 * `AddTeamMemberDialog` owns the invite request, its own validation and its own
 * toast; this step supplies `open`/`onClose`/`onSuccess` and counts successes.
 */

import { useState } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useTranslation } from 'react-i18next';
import { AddTeamMemberDialog } from '@/components/settings/AddTeamMemberDialog';

interface StaffStepProps {
  canManage: boolean;
  /** Rises as invitations are sent, so the step can confirm what happened. */
  invitedCount: number;
  onInvited: () => void;
}

export function StaffStep({ canManage, invitedCount, onInvited }: StaffStepProps) {
  const { t } = useTranslation('onboarding');
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('staff.intro')}
      </Typography>

      <Stack spacing={2} alignItems="flex-start">
        {canManage ? (
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            {t('staff.invite')}
          </Button>
        ) : (
          <Alert severity="info" sx={{ width: '100%' }}>
            {t('staff.noPermission')}
          </Alert>
        )}

        {invitedCount > 0 && (
          <Alert severity="success" sx={{ width: '100%' }}>
            {t('staff.invited', { count: invitedCount })}
          </Alert>
        )}
      </Stack>

      <AddTeamMemberDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={onInvited}
      />
    </Box>
  );
}
